<template>
  <div>
    <b-modal
      ref="roleUsersModal"
      centered
      size="lg"
      title="Manage Role Users"
      hide-footer
    >
      <div
        v-if="roleUsersState.loading"
        class="text-center py-2"
      >
        <b-spinner class="mr-1" />
        <span>Loading users...</span>
      </div>

      <div v-else>
        <b-row class="mb-1">
          <b-col cols="12">
            <h5 class="mb-25">
              {{ roleUsersState.role ? roleUsersState.role.title : 'Role' }}
            </h5>
            <small class="text-muted">
              Current members: {{ roleUsersState.assignedUsers.length }}
            </small>
          </b-col>
        </b-row>

        <b-row>
          <b-col
            cols="12"
            md="6"
          >
            <b-form-group :label="`Current Users (${roleUsersState.assignedUsers.length})`">
              <div style="border: 1px solid #eee; min-height: 240px; max-height: 240px; overflow-y: auto; padding: 8px;">
                <div
                  v-if="roleUsersState.assignedUsers.length === 0"
                  class="text-muted"
                >
                  No users are currently linked to this role.
                </div>

                <div
                  v-for="assignedUser in roleUsersState.assignedUsers"
                  :key="assignedUser.id"
                  class="py-50 border-bottom d-flex align-items-start"
                >
                  <b-form-checkbox
                    v-model="roleUsersState.selectedAssignedUserIds"
                    :value="assignedUser.id"
                    class="mt-25 mr-50"
                  />
                  <div>
                    <div>{{ assignedUser.displayName }}</div>
                    <small class="text-muted">{{ assignedUser.meta }}</small>
                  </div>
                </div>
              </div>
            </b-form-group>

            <small class="text-muted d-block mb-1">
              Select current members to remove them from this role.
            </small>
          </b-col>

          <b-col
            cols="12"
            md="6"
          >
            <b-form-group label="Add Users To Role">
              <v-select
                v-model="roleUsersState.selectedUsers"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="roleUsersState.availableUsers"
                label="displayName"
                multiple
                placeholder="Select users..."
              >
                <template #option="{ displayName, meta }">
                  <div>{{ displayName }}</div>
                  <small class="text-muted">{{ meta }}</small>
                </template>
              </v-select>
            </b-form-group>

            <small class="text-muted d-block mb-1">
              Selected users will be moved into this role when you add them.
            </small>

            <div style="border: 1px solid #eee; min-height: 165px; max-height: 165px; overflow-y: auto; padding: 8px;">
              <div
                v-if="roleUsersState.selectedUsers.length === 0"
                class="text-muted"
              >
                No users selected yet.
              </div>

              <div
                v-for="selectedUser in roleUsersState.selectedUsers"
                :key="selectedUser.id"
                class="py-50 border-bottom"
              >
                <div>{{ selectedUser.displayName }}</div>
                <small class="text-muted">{{ selectedUser.meta }}</small>
              </div>
            </div>
          </b-col>
        </b-row>

        <div class="d-flex justify-content-end mt-2">
          <b-button
            size="sm"
            variant="outline-secondary"
            class="mr-1"
            @click="closeRoleUsersModal"
          >
            Close
          </b-button>
          <b-button
            size="sm"
            variant="outline-danger"
            class="mr-1"
            :disabled="roleUsersState.saving || roleUsersState.selectedAssignedUserIds.length === 0"
            @click="removeRoleUsers"
          >
            <b-spinner
              v-if="roleUsersState.saving && roleUsersState.activeAction === 'remove'"
              small
              class="mr-1"
            />
            <span>Remove Selected</span>
          </b-button>
          <b-button
            size="sm"
            variant="primary"
            :disabled="roleUsersState.saving || roleUsersState.selectedUsers.length === 0"
            @click="submitRoleUsers"
          >
            <b-spinner
              v-if="roleUsersState.saving && roleUsersState.activeAction === 'add'"
              small
              class="mr-1"
            />
            <span v-if="!roleUsersState.saving">Add Selected</span>
            <span v-else>Saving...</span>
          </b-button>
        </div>
      </div>
    </b-modal>

    <!-- Form -->
    <b-card no-body>
      <b-card-body>
        <b-row>
          <b-col cols="12">
            <validation-observer
              ref="dataForm"
              #default="{ }"
            >
              <b-form
                ref="form"
                @submit.stop.prevent=""
              >
                <b-row>
                  <b-col
                    cols="12"
                    md="4"
                  >
                    <b-row>
                      <b-col cols="12">
                        <b-form-group
                          labe-for="title"
                          label="Name"
                        >
                          <validation-provider
                            #default="{ errors }"
                            name="Name"
                            rules=""
                          >
                            <b-form-input
                              id="title"
                              v-model="role.title"
                              name="title"
                              size="sm"
                              placeholder="Group name"
                              :state="errors.length > 0 ? false : null"
                              auto-focus
                            />
                            <small
                              v-if="errors"
                              class="text-danger"
                            >{{ errors[0] }}</small>
                            <small
                              v-if="serverErrors && serverErrors.title"
                              class="text-danger"
                            >
                              {{ serverErrors.title[0] }}
                            </small>
                          </validation-provider>
                        </b-form-group>
                      </b-col>
                    </b-row>
                    <b-row>
                      <b-col>
                        <b-form-checkbox
                          v-model="role.fieldOps"
                          checked="false"
                          class="custom-control-success"
                          name="fieldOps"
                          switch
                        >
                          <span class="switch-icon-left">
                            <feather-icon icon="CheckCircleIcon" />
                          </span>
                          <span>On Field Operations?</span>
                        </b-form-checkbox>
                      </b-col>
                    </b-row>
                    <b-row>
                      <b-col>
                        <b-form-checkbox
                          v-model="role.status"
                          checked="false"
                          class="custom-control-success"
                          name="status"
                          switch
                        >
                          <span class="switch-icon-left">
                            <feather-icon icon="CheckCircleIcon" />
                          </span>
                          <span>Is Active?</span>
                        </b-form-checkbox>
                      </b-col>
                    </b-row>
                    <b-row>
                      <b-col
                        cols="12"
                        md="6"
                      >
                        <b-button
                          size="sm"
                          variant="primary"
                          @click="submit"
                        >
                          <b-spinner
                            v-if="saving"
                            small
                            class="mr-1"
                            label="Small Spinner"
                          />
                          <span v-if="!saving">Save</span>
                          <span v-if="saving">Saving...</span>
                        </b-button>
                      </b-col>
                      <b-col
                        cols="12"
                        md="6"
                      >
                        <b-button
                          size="sm"
                          variant="outline-secondary"
                          @click="resetForm"
                        >
                          Reset
                        </b-button>
                      </b-col>
                    </b-row>
                  </b-col>
                  <b-col
                    cols="12"
                    md="8"
                  >
                    <b-row>
                      <b-col
                        cols="12"
                        md="6"
                        class="d-flex align-items-center justify-content-start mb-0 mb-md-0"
                      >
                        <h6>Permissions</h6>
                      </b-col>

                      <!-- Search -->
                      <b-col
                        cols="12"
                        md="6"
                      >
                        <div class="d-flex align-items-center justify-content-end">
                          <b-form-input
                            v-model="searchAbility"
                            class="d-inline-block mb-1"
                            placeholder="Search..."
                            size="sm"
                            @input="filterAbilities()"
                          />
                        </div>
                      </b-col>
                      <!-- ./Search -->
                    </b-row>
                    <div
                      style="border: 1px solid #eee;height:15vh;overflow-y:scroll;overflow-x: hidden;padding: 8px 8px;"
                    >
                      <b-row>
                        <b-col
                          v-for="(systemAbility, index) in systemAbilities"
                          :key="index"
                          cols="6"
                          md="3"
                        >
                          <b-form-checkbox
                            v-model="role.permissions"
                            :checked="false"
                            class="custom-control-success"
                            :value="systemAbility.id"
                            :label="systemAbility.title"
                          >
                            <small>{{ systemAbility.title }}</small>
                          </b-form-checkbox>
                        </b-col>
                      </b-row>
                    </div>
                    <b-row>
                      <b-col
                        cols="12"
                        class="mt-1 pl-2"
                      >
                        <b-form-checkbox
                          v-model="checkAll"
                          :checked="checkAll"
                          class="custom-control-success"
                          :value="!checkAll"
                          label="Asign All Permissions"
                          @change="checkAllAbilities()"
                        >
                          <small>Assign All Permissions</small>
                        </b-form-checkbox>
                      </b-col>
                    </b-row>
                  </b-col>
                </b-row>
              </b-form>
            </validation-observer>
          </b-col>
        </b-row>
      </b-card-body>
      <!-- <b-card-body>{{ role }}</b-card-body> -->
    </b-card>
    <!-- ./Form -->

    <!-- Table Container Card -->
    <b-card
      no-body
      class="mb-0"
    >
      <div class="m-2">
        <!-- Table Top -->
        <b-row>
          <!-- Per Page -->
          <b-col
            cols="12"
            md="6"
            class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
          >
            <label>Show</label>
            <v-select
              v-model="perPage"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="perPageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block mx-50 select-size-sm"
            />
            <label>entries</label>
          </b-col>
          <!-- ./Per Page -->

          <!-- Search -->
          <b-col
            cols="12"
            md="6"
          >
            <div class="d-flex align-items-center justify-content-end">
              <b-form-input
                v-model="searchQuery"
                class="d-inline-block mr-1"
                placeholder="Search..."
                size="sm"
              />
            </div>
          </b-col>
          <!-- ./Search -->
        </b-row>
        <!-- ./Table Top -->
      </div>

      <!-- Table -->
      <b-table
        ref="records"
        striped
        hover
        small
        class="position-relative"
        :items="fetch"
        responsive
        :fields="columns"
        primary-key="id"
        :sort-by.sync="sortBy"
        show-empty
        empty-text="No records found"
        :sort-desc.sync="isSortDirDesc"
      >
        <!-- Column: Status -->
        <template #cell(status)="data">
          <b-badge
            pill
            :variant="`light-${resolveStatusVariant(data.item.status)}`"
            class="text-capitalize"
          >
            {{ data.item.status ? 'Active' : 'Inactive' }}
          </b-badge>
        </template>

        <template #cell(fieldOps)="data">
          {{ data.item.fieldOps ? 'Yes' : 'No' }}
        </template>

        <template #cell(users_count)="data">
          <div class="role-users-count-cell">
            <b-badge
              pill
              variant="light-primary"
              class="role-users-count-badge"
            >
              {{ data.item.users_count || 0 }}
            </b-badge>
            <b-button
              size="sm"
              variant="outline-primary"
              class="role-users-manage-button"
              @click="openRoleUsersModal(data.item)"
            >
              Manage
            </b-button>
          </div>
        </template>

        <!-- Column: Actions -->
        <template #cell(actions)="data">
          <b-dropdown
            variant="link"
            no-caret
            :right="$store.state.appConfig.isRTL"
          >

            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>

            <b-dropdown-item @click="invokeUpdateForm(data.item)">
              <feather-icon icon="EditIcon" />
              <span class="align-middle ml-50">Edit</span>
            </b-dropdown-item>

            <b-dropdown-item @click="openRoleUsersModal(data.item)">
              <feather-icon icon="UsersIcon" />
              <span class="align-middle ml-50">Manage Users</span>
            </b-dropdown-item>

            <b-dropdown-item @click="remove(data.item.id)">
              <feather-icon icon="TrashIcon" />
              <span class="align-middle ml-50">Delete</span>
            </b-dropdown-item>
          </b-dropdown>
        </template>
      </b-table>

      <!-- Table Footer -->
      <div class="mx-2 mb-2">
        <b-row>
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-start"
          >
            <span class="text-muted">Showing {{ from }} to {{ to }} of {{ totalRecords }} entries</span>
          </b-col>
          <!-- Pagination -->
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-end"
          >
            <b-pagination
              v-model="currentPage"
              :total-rows="totalRecords"
              :per-page="perPage"
              first-number
              last-number
              class="mb-0 mt-1 mt-sm-0"
              prev-class="prev-item"
              next-class="next-item"
            >
              <template #prev-text>
                <feather-icon
                  icon="ChevronLeftIcon"
                  size="18"
                />
              </template>
              <template #next-text>
                <feather-icon
                  icon="ChevronRightIcon"
                  size="18"
                />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>

    </b-card>
    <!-- ./Table Container Card -->
  </div>
</template>

<script>
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import {
    BCard, BCardHeader, BCardBody,
    BRow, BCol,
    BForm, BFormGroup, BFormInput, BFormCheckbox, BDropdown, BDropdownItem,
    BButton, BSpinner, BModal,
    BTable, BPagination, BBadge, BAvatar,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import { required } from '@validations'
import store from '@/store'
import {
    ref, onUnmounted, onMounted,
} from '@vue/composition-api'
import groupsStoreModule from '@/views/cromis/role/groupsStoreModule'
import useGroupsList from '@/views/cromis/role/useGroupsList'
import usersStoreModule from '@/views/cromis/users/usersStoreModule'

export default {
    components: {
        BCard,
        BCardHeader,
        BCardBody,
        BRow,
        BCol,
        BForm,
        BFormGroup,
        BFormInput,
        BFormCheckbox,
        BDropdown,
        BDropdownItem,
        vSelect,
        BButton,
        BSpinner,
        BModal,
        BTable,
        BPagination,
        BBadge,
        BAvatar,
        ValidationProvider,
        ValidationObserver,
    },
    directives: {},
    props: {},

    setup(props, context) {
        const dataForm = ref(null)
        const saving = ref(false)
        const serverErrors = ref(null)
        const abilities = ref([])
        const systemAbilities = ref([])
        const previousAbilities = ref([])
        const searchAbility = ref(null)
        const checkAll = ref(false)
        const roleUsersModal = ref(null)
        const roleUsersState = ref({
            role: null,
            loading: false,
            saving: false,
            activeAction: null,
            assignedUsers: [],
            availableUsers: [],
            selectedUsers: [],
            selectedAssignedUserIds: [],
        })

        const role = ref({
            id: null,
            title: null,
            status: true,
            fieldOps: false,
            permissions: [],
        })

        const CROMIS_GROUP_STORE_MODULE_NAME = 'cromis-group'
        const CROMIS_USER_STORE_MODULE_NAME = 'cromis-user'

        // Register module
        if (!store.hasModule(CROMIS_GROUP_STORE_MODULE_NAME)) store.registerModule(CROMIS_GROUP_STORE_MODULE_NAME, groupsStoreModule)
        if (!store.hasModule(CROMIS_USER_STORE_MODULE_NAME)) store.registerModule(CROMIS_USER_STORE_MODULE_NAME, usersStoreModule)

        // UnRegister on leave
        onUnmounted(() => {
            if (store.hasModule(CROMIS_GROUP_STORE_MODULE_NAME)) store.unregisterModule(CROMIS_GROUP_STORE_MODULE_NAME)
            if (store.hasModule(CROMIS_USER_STORE_MODULE_NAME)) store.unregisterModule(CROMIS_USER_STORE_MODULE_NAME)
        })

        onMounted(async () => {
            await store.dispatch('cromis-group/abilities')
                .then(response => {
                    abilities.value = response.data.abilities
                    systemAbilities.value = abilities.value
                })
                .catch(error => {
                    console.log('got error', error)
                })
        })

        const {
            fetch,
            columns,
            perPage,
            currentPage,
            totalRecords,
            from,
            to,
            meta,
            perPageOptions,
            searchQuery,
            sortBy,
            isSortDirDesc,
            records,
            refetch,

            // UI
            // resolveUserRoleVariant,
            // resolveUserRoleIcon,
            resolveStatusVariant,
        } = useGroupsList()

        const getRequestErrorMessage = error => {
            if (error && error.response && error.response.data) {
                if (typeof error.response.data.message === 'string' && error.response.data.message.trim().length > 0) {
                    return error.response.data.message
                }

                if (typeof error.response.data.error === 'string' && error.response.data.error.trim().length > 0) {
                    return error.response.data.error
                }
            }

            if (error && typeof error.message === 'string' && error.message.trim().length > 0) {
                return error.message
            }

            return 'Something went wrong. See tech support'
        }

        const getUserPrimaryRoleId = user => {
            if (!user) return null

            if (Array.isArray(user.roles) && user.roles.length > 0) {
                const primaryRole = user.roles[0]

                if (primaryRole && primaryRole.id !== undefined && primaryRole.id !== null) return primaryRole.id
            }

            return user.role_id || null
        }

        const getUserPrimaryRoleTitle = user => {
            if (!user) return 'Unassigned'

            if (Array.isArray(user.roles) && user.roles.length > 0) {
                const primaryRole = user.roles[0]

                if (primaryRole && primaryRole.title) return primaryRole.title
            }

            return 'Unassigned'
        }

        const formatPersonName = person => {
            if (!person) return 'Unnamed User'

            const names = [person.firstName, person.middleName, person.lastName].filter(name => !!name)

            if (names.length > 0) return names.join(' ')

            return 'Unnamed User'
        }

        const getUserStationLabel = user => {
            if (!user || !user.station) return 'No station'

            const stationType = `${user.station.station_type || ''}`.toLowerCase()

            if (user.station.station && user.station.station.name) return user.station.station.name
            if (user.station.location && user.station.location.name && stationType.includes('location')) return user.station.location.name
            if (user.station.market && user.station.market.name) return user.station.market.name
            if (user.station.warehouse && user.station.warehouse.name) return user.station.warehouse.name

            return 'Head Office'
        }

        const buildRoleUserOption = user => ({
            ...user,
            displayName: `${formatPersonName(user.person)}${user.email || user.username ? ` (${user.email || user.username})` : ''}`,
            meta: `${getUserPrimaryRoleTitle(user)} | ${getUserStationLabel(user)}`,
        })

        const getStationType = station => {
            if (station && station.type !== undefined && station.type !== null) return Number(station.type)

            const stationType = `${station && station.station_type ? station.station_type : ''}`.toLowerCase()

            if (stationType.includes('location')) return 1
            if (stationType.includes('market')) return 2
            if (stationType.includes('warehouse')) return 3
            if (stationType.includes('line')) return 4

            return 0
        }

        const buildUserStationAssignment = station => {
            const stationType = getStationType(station)
            const stationRecord = station ? station.station || null : null
            let marketRecord = null
            let stationId = null
            let marketId = null

            if (station && station.market) marketRecord = station.market
            else if (stationRecord && stationRecord.market) marketRecord = stationRecord.market

            if (station && station.station_id) stationId = station.station_id
            else if (stationRecord) stationId = stationRecord.id

            if (station && station.market_id) marketId = station.market_id
            else if (marketRecord) marketId = marketRecord.id

            return {
                station_type: stationType,
                station_id: stationType === 0 ? JSON.parse(localStorage.getItem('userData')).client.id : stationId,
                market_id: stationType === 4 ? marketId : null,
            }
        }

        const buildUserRoleUpdatePayload = (userDetails, selectedRoleId) => {
            const stationAssignment = buildUserStationAssignment(userDetails.station)

            return {
                id: userDetails.id,
                firstName: userDetails.person ? userDetails.person.firstName : null,
                middleName: userDetails.person ? userDetails.person.middleName : null,
                lastName: userDetails.person ? userDetails.person.lastName : null,
                gender: userDetails.person ? (userDetails.person.gender || userDetails.person.sex || null) : null,
                isStaff: userDetails.isStaff !== undefined ? userDetails.isStaff : true,
                registrationNumber: userDetails.registrationNumber || null,
                staffID: userDetails.staffID || null,
                role_id: selectedRoleId,
                station_type: stationAssignment.station_type,
                station_id: stationAssignment.station_id,
                market_id: stationAssignment.market_id,
                permissions: userDetails.permissions || [],
                mobile: userDetails.mobile || null,
                email: userDetails.email || userDetails.username || null,
                password: null,
                password_confirmation: null,
                status: userDetails.status,
                pers: userDetails.pers || null,
            }
        }

        const loadRoleUsers = async selectedRole => {
            roleUsersState.value.loading = true
            roleUsersState.value.selectedUsers = []
            roleUsersState.value.selectedAssignedUserIds = []
            roleUsersState.value.assignedUsers = []
            roleUsersState.value.availableUsers = []

            try {
                const response = await store.dispatch('cromis-user/list', {
                    per_page: 1000,
                    page: 1,
                    query: '',
                })

                const fetchedUsers = response.data.users || response.data.data || []
                const assignedUsers = fetchedUsers
                    .filter(userItem => getUserPrimaryRoleId(userItem) === selectedRole.id)
                    .map(buildRoleUserOption)
                const availableUsers = fetchedUsers
                    .filter(userItem => getUserPrimaryRoleId(userItem) !== selectedRole.id)
                    .map(buildRoleUserOption)
                    .sort((firstUser, secondUser) => firstUser.displayName.localeCompare(secondUser.displayName))

                roleUsersState.value.assignedUsers = assignedUsers
                roleUsersState.value.availableUsers = availableUsers
            } catch (error) {
                context.root.$swal({
                    icon: 'error',
                    title: 'Server Error',
                    text: getRequestErrorMessage(error),
                    showConfirmButton: true,
                    customClass: {
                        confirmButton: 'btn btn-danger',
                    },
                    buttonsStyling: false,
                })
            } finally {
                roleUsersState.value.loading = false
            }
        }

        const closeRoleUsersModal = () => {
            roleUsersState.value = {
                role: null,
                loading: false,
                saving: false,
                activeAction: null,
                assignedUsers: [],
                availableUsers: [],
                selectedUsers: [],
                selectedAssignedUserIds: [],
            }

            if (roleUsersModal.value) roleUsersModal.value.hide()
        }

        const openRoleUsersModal = async selectedRole => {
            roleUsersState.value.role = selectedRole

            if (roleUsersModal.value) roleUsersModal.value.show()

            await loadRoleUsers(selectedRole)
        }

        const updateRoleUsers = async (selectedUsers, selectedRoleId, successMessage, actionKey) => {
            if (!roleUsersState.value.role || selectedUsers.length === 0) return

            roleUsersState.value.saving = true
            roleUsersState.value.activeAction = actionKey

            try {
                await Promise.all(selectedUsers.map(async selectedUser => {
                    const response = await store.dispatch('cromis-user/show', { id: selectedUser.id })
                    const userDetails = response.data.user || response.data
                    const payload = buildUserRoleUpdatePayload(userDetails, selectedRoleId)

                    return store.dispatch('cromis-user/update', { id: userDetails.id, data: payload })
                }))

                await loadRoleUsers(roleUsersState.value.role)
                refetch()

                context.root.$swal({
                    icon: 'success',
                    text: successMessage,
                    showConfirmButton: true,
                    timer: 4000,
                    customClass: {
                        confirmButton: 'btn btn-success',
                    },
                    buttonsStyling: false,
                })
            } catch (error) {
                context.root.$swal({
                    icon: 'error',
                    title: 'Server Error',
                    text: getRequestErrorMessage(error),
                    showConfirmButton: true,
                    customClass: {
                        confirmButton: 'btn btn-danger',
                    },
                    buttonsStyling: false,
                })
            } finally {
                roleUsersState.value.saving = false
                roleUsersState.value.activeAction = null
            }
        }

        const submitRoleUsers = async () => updateRoleUsers(
            roleUsersState.value.selectedUsers,
            roleUsersState.value.role ? roleUsersState.value.role.id : null,
            `Users have been linked to ${roleUsersState.value.role.title} successfully!`,
            'add',
        )

        const removeRoleUsers = async () => {
            const selectedUsers = roleUsersState.value.assignedUsers
                .filter(assignedUser => roleUsersState.value.selectedAssignedUserIds.includes(assignedUser.id))

            return updateRoleUsers(
                selectedUsers,
                null,
                `Users have been removed from ${roleUsersState.value.role.title} successfully!`,
                'remove',
            )
        }

        // Form Methods
        const invokeUpdateForm = item => {
            role.value = item
            previousAbilities.value = item.permissions
        }

        const filterAbilities = () => {
            if (searchAbility.value && searchAbility.value.length > 0) {
                systemAbilities.value = abilities.value.filter(ability => ability.title.toLowerCase().includes(searchAbility.value.toLowerCase()))
            } else {
                systemAbilities.value = abilities.value
            }
        }

        function checkAllAbilities() {
            if (checkAll.value) {
                role.value.permissions = abilities.value.map(ability => ability.id)
            } else {
                role.value.permissions = previousAbilities.value
            }
        }

        const resetForm = () => {
            role.value = {
                id: null,
                title: null,
                status: true,
                fieldOps: false,
                permissions: [],
            }

            previousAbilities.value = []
            checkAll.value = false
            checkAllAbilities()
        }

        const isFormValid = async () => {
            let isValid = false
            await dataForm.value.validate().then(success => {
                isValid = success
            })

            return isValid
        }

        async function handleCreate() {
            saving.value = true
            const valid = await isFormValid()

            if (!valid) {
                saving.value = false
                return
            }

            await store.dispatch('cromis-group/create', role.value)
                .then(response => {
                    refetch()
                    saving.value = false
                    resetForm()

                    context.root.$swal({
                        icon: 'success',
                        text: `User group ${response.data.title} has been saved successfully!`,
                        showConfirmButton: true,
                        timer: 5000,
                        customClass: {
                            confirmButton: 'btn btn-primary',
                        },
                        buttonsStyling: false,
                    })
                })
                .catch(error => {
                    saving.value = false
                    resetForm()
                    if (error.response.status === 422) {
                        serverErrors.value = error.response.data.errors
                    } else {
                        context.root.$swal({
                            icon: 'error',
                            title: 'Server Error',
                            text: 'Something went wrong. See tech support',
                            showConfirmButton: true,
                            customClass: {
                                confirmButton: 'btn btn-danger',
                            },
                            buttonsStyling: false,
                        })
                    }
                })
        }

        async function handleUpdate(item) {
            saving.value = true
            const valid = await isFormValid()

            if (!valid) {
                saving.value = false
                return
            }

            await store.dispatch('cromis-group/update', { id: item.id, data: item })
                .then(response => {
                    refetch()
                    saving.value = false

                    context.root.$swal({
                        icon: 'success',
                        text: `Changes to ${response.data.title} has been made successfully!`,
                        showConfirmButton: true,
                        timer: 3000,
                        customClass: {
                            confirmButton: 'btn btn-success',
                        },
                        buttonsStyling: false,
                    })
                    resetForm()
                })
                .catch(error => {
                    saving.value = false
                    resetForm()
                    console.log(error)
                    if (error.response.status === 422) {
                        serverErrors.value = error.response.data.errors
                    } else {
                        context.root.$swal({
                            icon: 'error',
                            title: 'Server Error',
                            text: 'Something went wrong. See tech support',
                            showConfirmButton: true,
                            customClass: {
                                confirmButton: 'btn btn-danger',
                            },
                            buttonsStyling: false,
                        })
                    }
                })
        }

        const submit = bvModalEvt => {
            bvModalEvt.preventDefault()
            serverErrors.value = null

            // Handle form submit
            if (role.value.id === null || role.value.id === 0) handleCreate()
            else handleUpdate(role.value)
        }

        const remove = async id => {
            await store.dispatch('cromis-group/remove', id)
                .then(() => {
                    refetch()
                })
                .catch(() => {
                    context.root.$swal({
                        icon: 'error',
                        title: 'Server Error',
                        text: 'Something went wrong. See tech support',
                        showConfirmButton: false,
                        customClass: {
                            confirmButton: 'btn btn-danger',
                        },
                        buttonsStyling: false,
                    })
                })
        }

        return {
            // Collections
            abilities,
            systemAbilities,
            previousAbilities,
            searchAbility,
            checkAllAbilities,
            checkAll,
            roleUsersModal,
            roleUsersState,
            openRoleUsersModal,
            closeRoleUsersModal,
            submitRoleUsers,
            removeRoleUsers,

            // Form
            dataForm,
            saving,
            serverErrors,
            role,
            isFormValid,
            submit,
            handleCreate,
            invokeUpdateForm,
            handleUpdate,
            remove,
            resetForm,
            filterAbilities,

            // Validations
            required,

            // Table
            fetch,
            columns,
            perPage,
            currentPage,
            totalRecords,
            from,
            to,
            meta,
            perPageOptions,
            searchQuery,
            sortBy,
            isSortDirDesc,
            records,
            refetch,
            resolveStatusVariant,
        }
    },
}
</script>

<style lang="scss" scoped>
@import '@core/scss/vue/libs/vue-select.scss';

.role-users-count-cell {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    width: 100%;
    min-width: 9.5rem;
}

.role-users-count-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 2.75rem;
}

.role-users-manage-button {
    min-width: 5.75rem;
    justify-content: center;
}
</style>
